import axios, { AxiosResponse } from "axios";
import { useAtom } from "jotai";
import React from "react";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import { ISalesAccess, ISalesAccessState, salesAccessAtom, SalesLevel } from "./types";

const crmUrl = `${environment.v2Url}/crm`;

export type SalesAccessKeyFunctions = {
    /** Resolves the current user's sales access; safe to call repeatedly. */
    resolve: () => Promise<void>;
    /** Forces a re-fetch, e.g. after a manager changes their own grant. */
    refresh: () => Promise<void>;
};

export type UseSalesAccess = [
    {
        status: ISalesAccessState['status'];
        access: ISalesAccess | null;
        isRep: boolean;
        isManager: boolean;
        loading: boolean;
    },
    SalesAccessKeyFunctions
];

/**
 * Sales access for the logged-in user.
 *
 * A 403 here is the expected answer for most users, not an error — the endpoint
 * is mounted behind RequireSalesAccess, so "no grant" arrives as 403. It is
 * mapped to `denied` and must not trigger the shared logout-on-401 handling.
 *
 * The result is cached in a jotai atom so the dashboard resolves it once rather
 * than once per component that asks.
 */
export const useSalesAccess = (): UseSalesAccess => {
    const [, authToken] = useLogin();
    const [state, setState] = useAtom(salesAccessAtom);

    const fetchAccess = React.useCallback(async (force: boolean): Promise<void> => {
        if (!authToken) {
            setState({ status: 'denied', access: null });
            return;
        }

        if (!force && (state.status === 'resolving' || state.status === 'granted' || state.status === 'denied')) {
            return;
        }

        setState({ status: 'resolving', access: null });

        try {
            const response: AxiosResponse<ISalesAccess> = await axios.get(`${crmUrl}/me`, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            setState({ status: 'granted', access: response.data });
        } catch (err: any) {
            const status = err?.response?.status;

            // 403 is the normal answer for a user without a grant.
            if (status === 403 || status === 401) {
                setState({ status: 'denied', access: null });
                return;
            }

            // Anything else is a real failure. Keep it distinct from `denied` so
            // the nav can retry rather than hiding the tab forever.
            setState({ status: 'error', access: null });
        }
        // state.status is intentionally excluded: including it would rebuild the
        // callback on every transition and re-fire the guarded effects below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, setState]);

    const resolve = React.useCallback(() => fetchAccess(false), [fetchAccess]);
    const refresh = React.useCallback(() => fetchAccess(true), [fetchAccess]);

    const level: SalesLevel | undefined = state.access?.level;

    return [
        {
            status: state.status,
            access: state.access,
            isRep: state.status === 'granted' && (level === 'rep' || level === 'manager'),
            isManager: state.status === 'granted' && level === 'manager',
            loading: state.status === 'unresolved' || state.status === 'resolving'
        },
        { resolve, refresh }
    ];
};
